import { Flex, Tag, Text } from '@spotify-internal/encore-web';
import {
  LabelUtils,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import { RuleFragment, getTypeOrNull } from '@spotify/confidence-graphql';
import { getRuleTypeDisplayName } from '@spotify/plugin-confidence-assignment-rules-react';
import { formatRuleId } from '@spotify/plugin-confidence-flags-react';

import styles from './RuleItem.module.css';

const getRuleVariantNames = (rule: RuleFragment) =>
  (rule.assignmentSpec?.assignments ?? [])
    .map(assignment => getTypeOrNull(assignment.variant?.variant)?.name)
    .filter((name): name is string => Boolean(name))
    .map(name => extractLastNameComponent(name) ?? name);

interface RuleItemProps {
  rule: RuleFragment;
}

export const RuleItem = ({ rule }: RuleItemProps) => {
  const id = formatRuleId(rule.name);
  const ruleTypeRaw = LabelUtils.getLabelValue(rule.labels ?? [], 'ruleType');
  const ruleType =
    getRuleTypeDisplayName(ruleTypeRaw) || 'Conditional targeting';
  const variantNames = getRuleVariantNames(rule);
  const targetingKey = rule.targetingKeySelector;

  return (
    <Flex direction="column" className={styles.ruleItem} gap="tighter-4">
      <Flex alignItems="center" gap="tighter-2">
        <Text variant="bodySmallBold" className={styles.ruleId}>
          {id}
        </Text>
        <Text variant="bodySmall" semanticColor="textSubdued">
          {ruleType}
        </Text>
        {rule.enabled === false && (
          <Text variant="bodySmall" semanticColor="textSubdued">
            (disabled)
          </Text>
        )}
      </Flex>
      {rule.description && <Text variant="bodySmall">{rule.description}</Text>}
      {targetingKey && (
        <Flex alignItems="center" gap="tighter-2">
          <Text variant="bodySmall" semanticColor="textSubdued">
            Randomized by
          </Text>
          <Flex wrap="wrap" gap="tighter-2" aria-label="Randomization key">
            <Tag>{targetingKey}</Tag>
          </Flex>
        </Flex>
      )}
      {variantNames.length > 0 && (
        <Text variant="bodySmall" semanticColor="textSubdued">
          {`Assigns: ${variantNames.join(', ')}`}
        </Text>
      )}
      {(() => {
        const environments = (rule.environments ?? [])
          .map(env => getTypeOrNull(env))
          .filter((env): env is NonNullable<typeof env> => Boolean(env));
        if (environments.length === 0) return null;
        return (
          <Flex alignItems="center" gap="tighter-2">
            <Text variant="bodySmall" semanticColor="textSubdued">
              Environments:
            </Text>
            <Flex wrap="wrap" gap="tighter-2" aria-label="Environments">
              {environments.map(env => {
                const envLabel = extractLastNameComponent(env.name) ?? env.name;
                return <Tag key={env.name}>{envLabel}</Tag>;
              })}
            </Flex>
          </Flex>
        );
      })()}
    </Flex>
  );
};
